import cn from 'classnames'
import moment from 'moment'
import Image from 'next/image'
import React from 'react'
import {
  IContentItemFragment,
  IEnum_Content_Contenttype,
  ISolutionArticleFragment,
  Maybe,
} from 'src/graphql/generated/hooks'
import { getContentWriterInformation } from 'src/hooks/useContentWriterInformation'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { normalizeWritersList } from 'src/utils/normalizeContentWriters'

import { PremiumContentIcon } from '../global/PremiumContentIcon/PremiumContentIcon'

export interface ArticleItemProps {
  article: Maybe<ISolutionArticleFragment> | Maybe<IContentItemFragment>
  className?: string
  from?: string
}
export const ArticleItem: React.FC<ArticleItemProps> = ({ article, className, from = 'articles', ...props }) => {
  const title = article?.title || (article as IContentItemFragment)?.title
  const date =
    from === 'articles'
      ? (article as ISolutionArticleFragment)?.date
        ? moment((article as ISolutionArticleFragment)?.date).format('MMMM DD, YYYY')
        : null
      : (article as IContentItemFragment)?.publishedTime
        ? moment((article as IContentItemFragment)?.publishedTime).format('MMMM DD, YYYY')
        : null
  const ci = article as IContentItemFragment
  const articleWriterLine =
    from !== 'articles'
      ? normalizeWritersList(ci.writers?.length ? ci.writers : ci.writer)
          .map((w) => getContentWriterInformation(w)?.name)
          .filter(Boolean)
          .join(' and ')
      : ''
  const defaultLogo = useDefaultLogo()

  const contentType =
    from === 'contents' && (article as IContentItemFragment)?.contentType
      ? (article as IContentItemFragment)?.contentType
      : IEnum_Content_Contenttype.VendorResource

  const defaultImageUrl = from === 'contents' && (article as any)?.defaultImage ? (article as any).defaultImage : null
  const shouldShowDefaultImage =
    contentType &&
    [
      IEnum_Content_Contenttype.Blog,
      IEnum_Content_Contenttype.IndustryAnalysis,
      IEnum_Content_Contenttype.VendorResource,
      IEnum_Content_Contenttype.KnowHow,
      IEnum_Content_Contenttype.LegaltechResourcesForLawyers,
      IEnum_Content_Contenttype.BuyersGuide,
      IEnum_Content_Contenttype.PremiumResource,
    ].includes(contentType)

  const logo = useDefaultLogo(defaultImageUrl)

  const logoContent =
    from === 'contents' ? (
      <>
        {shouldShowDefaultImage && defaultImageUrl ? (
          <Image
            className="bg-white rounded-l-[1rem] object-cover box-border rounded-[1rem]"
            src={defaultImageUrl}
            alt={title}
            width="118"
            height="88"
            sizes="33vw"
            style={{ height: '120px' }}
            priority
          />
        ) : contentType ? (
          <PremiumContentIcon contentType={contentType} size="118" defaultImage={defaultImageUrl} />
        ) : (
          <Image
            className="bg-white rounded-l-[1rem] object-cover box-border rounded-[1rem]"
            src={logo}
            alt={title}
            width="118"
            height="88"
            sizes="33vw"
            style={{ height: '120px' }}
            priority
          />
        )}
      </>
    ) : (
      <Image
        className="bg-white rounded-l-[1rem] object-cover box-border rounded-[1rem]"
        src={logo}
        alt={title}
        width="118"
        height="88"
        sizes="33vw"
        style={{ height: '120px' }}
        priority
      />
    )

  return (
    <div
      className={cn('flex items-center m-[1rem] rounded-[1rem] border border-mist cursor-pointer', className)}
      {...props}
    >
      <div className="flex flex-row items-center w-full h-full">
        <div
          className={cn(
            from === 'contents'
              ? 'h-full flex items-center justify-start min-w-[118px] w-auto pr-[42px]'
              : 'w-1/4 h-full flex items-center justify-center',
          )}
        >
          {from === 'articles' ? (
            (article as ISolutionArticleFragment)?.image?.url ? (
              <Image
                className="bg-white rounded-l-[1rem] mr-[1rem] object-cover box-border"
                src={(article as ISolutionArticleFragment)?.image?.url || ''}
                priority
                alt=""
                width="118"
                height="88"
                sizes="33vw"
                style={{ height: '120px' }}
              />
            ) : (
              <Image
                className="bg-white rounded-l-[1rem] mr-[1rem] p-[16px] box-border"
                src={defaultLogo}
                priority
                alt=""
                width="118"
                height="88"
              />
            )
          ) : (
            <></>
          )}

          {from === 'contents' ? (article as IContentItemFragment)?.defaultImage ? logoContent : logoContent : <></>}
        </div>
        {from === 'articles' ? (
          <div className="flex flex-col font-primary font-regular p-1 pr-2 justify-between w-3/4">
            {(article as ISolutionArticleFragment)?.title && (
              <p className="text-[1.125rem] text-darkgray line-clamp-2 h-[60px]">
                {(article as ISolutionArticleFragment).title}
              </p>
            )}
            <div className="flex flex-wrap mb-1">
              {(article as ISolutionArticleFragment)?.publishedBy && (
                <div className="cursor-pointer font-medium">{(article as ISolutionArticleFragment)?.publishedBy}</div>
              )}

              {(article as ISolutionArticleFragment)?.publishedBy && date && (
                <div className="ml-[0.25rem] mr-[0.25rem] font-medium">|</div>
              )}

              {date && <div className="font-medium">{date}</div>}
            </div>
          </div>
        ) : (
          <div className="flex flex-col font-primary font-regular p-1 pr-2 justify-between w-3/4">
            {(article as IContentItemFragment)?.title && (
              <p className="text-[1.125rem] text-darkgray line-clamp-2 h-[60px]">
                {(article as IContentItemFragment).title}
              </p>
            )}
            <div className="flex flex-wrap mb-1">
              {articleWriterLine ? <div className=" cursor-pointer font-medium">{articleWriterLine}</div> : null}

              {articleWriterLine && date && <div className="ml-[0.25rem] mr-[0.25rem] font-medium">|</div>}

              {date && <div className="font-medium">{date}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
